export const SIMPLE_NOTES_TEXT = Object.freeze({
  page: {
    title: 'Notas simples',
    subtitle: 'Listado de notas simples emitidas sin CFDI.',
  },
  create: {
    page: {
      title: 'Emitir nota simple',
      subtitle: 'Genera una nueva nota simple con la información del cliente y sus conceptos.',
    },
    ui: {
      customerCard: {
        title: 'Cliente',
        toggleNew: '+ Nuevo cliente',
        toggleHide: 'Ocultar',
        fieldLabel: 'Cliente',
        selectorPlaceholder: 'Buscar cliente',
        inputPlaceholder: 'Nombre del cliente',
        saveButton: 'Guardar',
        savingButton: 'Guardando...',
        cancelButton: 'Cancelar',
        noOptions: 'No hay opciones.',
      },
      infoCard: {
        title: 'Información de nota simple',
        fields: {
          date: 'Fecha',
          folio: 'Folio',
          paymentForm: 'Forma de pago',
        },
        placeholders: {
          date: 'Selecciona una fecha',
          paymentForm: 'Selecciona forma de pago',
        },
        values: {
          todayButton: 'Hoy',
          defaultSeries: 'NS',
          emptyFolio: 'NS',
          fallbackPaymentFormOption: '01 - Efectivo',
        },
      },
      conceptsCard: {
        title: 'Conceptos',
        addButton: 'Concepto',
        headers: {
          qty: 'Cantidad',
          unit: 'Unidad',
          concept: 'Concepto',
          unitValue: 'Valor unitario',
          amount: 'Importe',
          actions: 'Acciones',
        },
        empty: 'No hay conceptos capturados.',
        actions: {
          edit: 'Editar concepto',
          delete: 'Eliminar concepto',
        },
      },
      comments: {
        title: 'Comentarios',
        placeholder: 'Agrega comentarios para la nota simple',
      },
      totals: {
        total: 'Total',
      },
    },
    headerActions: {
      back: 'Volver',
    },
    validation: {
      customerRequired: 'Debes seleccionar un cliente.',
      conceptsRequired: 'Debes capturar al menos un concepto válido.',
      customerRequiredForPlans: 'Debes seleccionar un cliente para cargar sus planes de pago.',
    },
    toast: {
      conceptsCleared: 'Borrador limpiado.',
      previewPending: 'La vista previa estará disponible en el siguiente paso.',
      previewError: 'No fue posible generar la vista previa de la nota simple.',
      customerCreated: 'Cliente creado correctamente.',
      customerCreateError: 'No fue posible crear el cliente.',
      created: 'Nota simple creada correctamente.',
      createError: 'No fue posible crear la nota simple.',
    },
  },
  exports: {
    modal: {
      title: 'Exportar notas simples',
      subtitle: 'Selecciona la información que deseas incluir en el archivo Excel. Puedes guardar esta configuración para utilizarla nuevamente.',
      format: {
        title: 'Formato de exportación',
        selectorLabel: 'Formato',
        selectorPlaceholder: 'Selecciona una opción',
        newOption: 'Nuevo formato',
        nameLabel: 'Nombre del formato',
        namePlaceholder: 'Ej. Resumen por cliente',
        nameRequired: 'Debes capturar el nombre del formato.',
        duplicateConfirm: 'Ya existe un formato con ese nombre. ¿Deseas reemplazarlo?',
      },
      columns: {
        title: 'Columnas a exportar',
        subtitle: 'Activa las columnas que deseas incluir en el archivo. El orden de selección se respeta en el Excel.',
        selectAll: 'Todas',
        clearSelection: 'Limpiar',
        restoreDefault: 'Restaurar',
        selectedCount: (count) => `${count} columna${count === 1 ? '' : 's'} seleccionada${count === 1 ? '' : 's'}`,
        warningsByGroup: {},
        orderTitle: 'Orden de columnas',
        orderSubtitle: 'Ajusta el orden con los controles de subir y bajar.',
        orderEmpty: 'No hay columnas seleccionadas.',
      },
      groups: {
        general: 'Información general',
        customer: 'Cliente',
        business: 'Negocio',
        financial: 'Montos y pagos',
      },
      footer: {
        cancel: 'Cancelar',
        saveAndExport: 'Guardar y exportar',
        exportOnly: 'Exportar',
        exporting: 'Exportando...',
      },
      toast: {
        loadError: 'No fue posible cargar la configuración de exportación.',
        noColumns: 'Debes seleccionar al menos una columna para exportar.',
        formatSaved: 'Formato de exportación guardado correctamente.',
        formatUpdated: 'Formato de exportación actualizado correctamente.',
        formatDeleted: 'Formato de exportación eliminado correctamente.',
        formatError: 'No fue posible guardar el formato de exportación.',
        formatDeleteError: 'No fue posible eliminar el formato de exportación.',
        exportSuccess: 'Exportación de notas simples generada correctamente.',
        exportError: 'No fue posible exportar las notas simples.',
      },
    },
    settings: {
      deleteModal: {
        title: 'Eliminar formato de exportación',
        subtitle: 'Confirma para continuar con esta operación.',
        message: (formatName) => `Formato: ${formatName}. ¿Deseas eliminar este formato de exportación? Esta acción no se puede deshacer.`,
        confirm: 'Eliminar',
      },
      table: {
        actions: {
          delete: 'Eliminar',
        },
      },
    },
  },
  headerActions: {
    create: 'Nota simple',
    exportExcel: 'Exportar',
    exportDownload: 'Descargar',
  },
  toolbar: {
    searchPlaceholder: 'Buscar por folio, cliente o negocio',
    searchButtonTitle: 'Buscar notas simples',
    filtersButtonTitle: 'Mostrar filtros',
    emptyOption: 'Sin opciones',
    allOptionLabels: {
      customer: 'Todos los clientes',
      business: 'Todos los negocios',
      area: 'Todas las áreas',
    },
    selectionAllLabels: {
      customer: 'Todos los clientes',
      business: 'Todos los negocios',
      area: 'Todas las áreas',
    },
    dateRangePresets: [
      { key: 'this_month', label: 'Este mes' },
      { key: 'last_month', label: 'Mes anterior' },
      { key: 'this_year', label: 'Año actual' },
      { key: 'last_year', label: 'Año anterior' },
    ],
    filters: {
      customer: {
        label: 'Cliente',
        placeholder: 'Cliente',
        searchable: true,
      },
      business: {
        label: 'Negocio',
        placeholder: 'Negocio',
        searchable: true,
      },
      area: {
        label: 'Área',
        placeholder: 'Área',
        searchable: true,
      },
    },
  },
  tabs: {
    all: 'Vigentes',
    pending: 'Pendientes',
    partial: 'Parcialmente pagadas',
    paid: 'Pagadas',
    canceled: 'Canceladas',
  },
  metrics: {
    totalInvoiced: 'Total notas',
    totalCollected: 'Total cobrado',
    pendingBalance: 'Saldo pendiente',
    averagePerNote: 'Promedio por nota',
  },
  table: {
    loading: 'Cargando notas simples...',
    empty: 'No hay notas simples para mostrar.',
    columns: {
      date: 'FECHA',
      customer: 'CLIENTE',
      folio: 'FOLIO',
      total: 'TOTAL',
      balance: 'SALDO',
      status: 'ESTADO',
      payment: 'PAGO',
    },
    sortTitles: {
      date: 'Ordenar por fecha',
      customer: 'Ordenar por cliente',
      folio: 'Ordenar por folio',
      total: 'Ordenar por total',
      balance: 'Ordenar por saldo',
    },
    actions: {
      view: 'Ver nota',
      pay: 'Pagar',
      download: 'Descargar PDF',
    },
    links: {
      customerDetail: 'Abrir detalle del cliente',
      saleDetail: 'Abrir detalle del negocio',
    },
    values: {
      emptyValue: '-',
      noBusiness: 'Sin negocio relacionado',
      paid: 'Pagado',
      pending: 'Pendiente',
    },
  },
  toasts: {
    exportExcelPending: 'La exportación a Excel estará disponible en el siguiente paso.',
    downloadRequestCreated: 'Tu solicitud de descarga fue registrada y comenzará a procesarse en segundo plano.',
    downloadRequestError: 'No fue posible preparar la descarga de documentos.',
    downloadPackageError: 'No fue posible descargar el paquete seleccionado.',
    retryPackageSuccess: 'El paquete se marcó para reintento.',
    retryPackageError: 'No fue posible reintentar el paquete.',
    regenerateSuccess: 'Se generó una nueva solicitud con los parámetros de la descarga anterior.',
    regenerateError: 'No fue posible generar nuevamente la descarga.',
    missingRowForDownload: 'No se pudo identificar la nota simple a descargar.',
    downloadDocumentsError: 'No fue posible descargar el PDF de la nota simple #:reference.',
  },
  downloadModal: {
    title: 'Descargar documentos',
    closeAriaLabel: 'Cerrar modal de descarga de documentos',
    contextMessage: 'Se descargarán los documentos correspondientes a los resultados de la pestaña y filtros actuales.',
    lastDownloadTitle: 'Última descarga',
    fallbackScopeLabel: 'Notas simples',
    resultsFound: (count) => `Se encontraron ${count} notas simples con los filtros actuales.`,
    noResultsMessage: 'No se encontraron notas simples para proceder con la descarga.',
    documentTypeLabel: 'Tipo de documento a descargar',
    documentTypes: {
      pdf: 'PDF',
      xml: 'XML',
      both: 'PDF + XML',
    },
    groupByLabel: 'Agrupar por:',
    groupByOptions: {
      customer: 'Cliente',
      month: 'Mes',
      none: 'Sin agrupar',
    },
    packageLimitHint: (limit) => `Límite por paquete: ${limit} notas simples. Si se supera, se generarán múltiples paquetes automáticamente.`,
    expiryHint: (hours) => `Disponible durante ${hours} horas.`,
    availableUntil: (value) => `Disponible hasta ${value}.`,
    summaryLine: (documentType, count) => `${documentType} · ${count} notas simples`,
    packageLine: (index, total) => `Paquete ${index} de ${total}`,
    preparing: {
      title: 'Preparando tu descarga',
      keepWorking: 'Puedes esperar aquí o cerrar esta ventana y continuar trabajando. La descarga seguirá preparándose aunque cierres esta ventana.',
      packageProgress: (completed, total) => `Preparando paquete ${Math.min(completed + 1, Math.max(total, 1))} de ${Math.max(total, 1)}...`,
    },
    expiredMessage: 'Descarga expirada. Los archivos de esta descarga ya no están disponibles.',
    status: {
      pending: 'Pendiente',
      processing: 'Preparando',
      completed: 'Listo',
      partialFailed: 'Parcial con errores',
      failed: 'Error',
      expired: 'Expirada',
    },
    actions: {
      close: 'Cerrar',
      refresh: 'Actualizar estado',
      download: 'Descargar',
      downloading: 'Descargando...',
      downloadPackage: 'Descargar',
      retryPackage: 'Reintentar',
      regenerate: 'Generar nuevamente',
    },
  },
  detail: {
    page: {
      title: 'Nota simple',
    },
    loading: 'Cargando nota simple...',
    loadError: 'No fue posible cargar la nota simple.',
    sectionTabsAriaLabel: 'Secciones del detalle de nota simple',
    tabs: {
      detail: 'Detalle',
      shipping: 'Envíos',
      documents: 'Documentos',
      history: 'Historial',
    },
    breadcrumb: {
      root: 'Notas simples',
      ariaLabel: 'Ruta de nota simple',
      backLabel: 'Volver',
    },
    status: {
      active: 'VIGENTE',
      canceled: 'CANCELADA',
    },
    header: {
      moreActionsTitle: 'Más acciones',
      cancelImportantHint: 'Esta acción cancela la nota simple y no se puede deshacer.',
    },
    headerActions: {
      pay: 'Pagar',
      download: 'Descargar',
      send: 'Enviar',
    },
    cancelConfirm: {
      title: 'Cancelar nota simple',
      subtitle: 'Confirma para continuar con esta operación.',
      message: 'La nota simple se cancelará y sus planes de pago volverán a pendiente. Esta acción no se puede deshacer.',
      confirm: 'Sí, cancelar nota',
      cancel: 'Volver',
    },
    cards: {
      operation: {
        title: 'Operación',
        fields: {
          status: 'Estado',
          relatedPayments: 'Pago',
          balance: 'Saldo',
          pendingBalance: 'Saldo pendiente',
          customerShort: 'Cliente',
          customer: 'Cliente asociado',
          sale: 'Negocio asociado',
          sent: 'Enviada',
          lastSent: 'Último envío',
          comments: 'Comentarios',
        },
        values: {
          sent: 'Enviada',
          notSent: 'Sin enviar',
          noLastSent: 'Sin registro',
          noCustomer: 'Sin cliente relacionado',
          noSale: 'Sin negocio relacionado',
        },
        actions: {
          showMore: 'Ver más',
          showLess: 'Ver menos',
        },
      },
      totals: {
        fields: {
          total: 'TOTAL',
        },
      },
      summary: {
        title: 'Resumen',
        fields: {
          serialFolio: 'Folio',
          date: 'Fecha',
          currency: 'Moneda',
          exchangeRate: 'Tipo de cambio',
          type: 'Tipo',
          payment: 'Método de pago',
        },
        values: {
          simpleNote: 'Nota simple',
        },
      },
    },
    toast: {
      loadError: 'No fue posible consultar la nota simple.',
      actions: {
        download: 'No fue posible descargar el PDF de la nota simple.',
        send: 'Envío disponible en el siguiente paso.',
        cancel: 'No fue posible cancelar la nota simple.',
      },
    },
    tabPanels: {
      documents: {
        subtitle: 'Listado de archivos y documentos relacionados con esta nota simple.',
      },
    },
  },
  history: {
    title: 'Historial de la nota simple',
    subtitle: 'Registro cronológico de movimientos y cambios de la nota simple.',
    loading: 'Cargando historial de la nota simple...',
    empty: 'Sin movimientos registrados en el historial.',
    table: {
      headers: {
        index: '#',
        date: 'Fecha',
        module: 'Módulo',
        event: 'Evento',
        description: 'Descripción',
        user: 'Usuario',
      },
    },
    fallback: {
      date: '-',
      module: 'General',
      event: 'Cambio',
      description: 'Actividad registrada',
      actor: 'Sistema',
    },
  },
  shipping: {
    title: 'Envíos de la nota simple',
    subtitle: 'Seguimiento de intentos de envío, estatus y responsables.',
    loading: 'Cargando envíos de la nota simple...',
    empty: 'Sin envíos registrados para esta nota simple.',
    table: {
      headers: {
        index: '#',
        date: 'Fecha',
        module: 'Canal',
        event: 'Estatus',
        description: 'Descripción',
        user: 'Usuario',
      },
    },
    fallback: {
      date: '-',
      module: 'Correo',
      event: 'Pendiente',
      description: 'Envío registrado',
      actor: 'Sistema',
    },
  },
});
